import React, { useState, useEffect } from 'react';
import { X, Plus, Check, Key, Calendar, Users, Package, Trash2, Edit2, RefreshCw, AlertTriangle } from 'lucide-react';
import { licenseAPI } from '../api';
import { useLicense } from '../contexts/LicenseContext';

const LicenseAdmin = () => {
  const { activateLicense: contextActivateLicense, refreshLicense } = useLicense();
  const [licenses, setLicenses] = useState([]);
  const [modules, setModules] = useState({});
  const [currentLicense, setCurrentLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activateKey, setActivateKey] = useState('');
  const [activateError, setActivateError] = useState('');
  const [newLicense, setNewLicense] = useState({
    client_name: '',
    client_email: '',
    modules: [],
    expiration_date: '',
    max_users: 5,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [licensesRes, modulesRes, currentRes] = await Promise.all([
        licenseAPI.getAll(),
        licenseAPI.getModules(),
        licenseAPI.getCurrent()
      ]);
      setLicenses(licensesRes.data);
      setModules(modulesRes.data);
      setCurrentLicense(currentRes.data);
    } catch (err) {
      console.error('Error loading license data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newLicense.client_name || !newLicense.client_email || !newLicense.expiration_date || newLicense.modules.length === 0) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      await licenseAPI.create(newLicense);
      setShowCreateModal(false);
      setNewLicense({ client_name: '', client_email: '', modules: [], expiration_date: '', max_users: 5, notes: '' });
      loadData();
    } catch (err) {
      alert('Erreur lors de la création de la licence');
    }
  };

  const handleActivate = async () => {
    setActivateError('');
    try {
      await contextActivateLicense(activateKey);
      setShowActivateModal(false);
      setActivateKey('');
      loadData();
    } catch (err) {
      setActivateError(err.response?.data?.detail || 'Clé invalide');
    }
  };

  const handleDelete = async (licenseId) => {
    if (!window.confirm('Supprimer cette licence ?')) return;
    try {
      await licenseAPI.delete(licenseId);
      loadData();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const toggleModule = (moduleKey) => {
    setNewLicense(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleKey)
        ? prev.modules.filter(m => m !== moduleKey)
        : [...prev.modules, moduleKey]
    }));
  };

  const getExpirationStatus = (expDate) => {
    const exp = new Date(expDate);
    const today = new Date();
    const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return { status: 'expired', label: 'Expirée', color: 'var(--red)' };
    if (days <= 30) return { status: 'warning', label: `${days} jours`, color: 'var(--orange)' };
    return { status: 'active', label: `${days} jours`, color: 'var(--green)' };
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('fr-CH');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <div data-testid="license-admin">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">Gestion des Licences</h2>
          <p className="text-sm text-[var(--text3)]">
            Gérez les licences et modules activés
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            className="btn btn-ghost"
            onClick={() => setShowActivateModal(true)}
            data-testid="activate-license-btn"
          >
            <Key size={14} /> Activer une licence
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-license-btn"
          >
            <Plus size={14} /> Nouvelle licence
          </button>
        </div>
      </div>

      {/* Current License Status */}
      {currentLicense && (
        <div className={`p-4 rounded-lg mb-6 ${currentLicense.is_expired ? 'bg-[var(--red)]/10 border border-[var(--red)]/30' : 'bg-[var(--green)]/10 border border-[var(--green)]/30'}`}>
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-full ${currentLicense.is_expired ? 'bg-[var(--red)]/20' : 'bg-[var(--green)]/20'}`}>
              {currentLicense.is_expired ? <AlertTriangle size={24} style={{ color: 'var(--red)' }} /> : <Check size={24} style={{ color: 'var(--green)' }} />}
            </div>
            <div className="flex-1">
              <div className="font-bold text-lg">
                {currentLicense.has_license ? currentLicense.client_name : 'Aucune licence active'}
              </div>
              {currentLicense.has_license && (
                <div className="text-sm text-[var(--text3)]">
                  Clé: <span className="font-mono">{currentLicense.license_key}</span>
                  {' • '}
                  {currentLicense.is_expired 
                    ? <span style={{ color: 'var(--red)' }}>Expirée le {fmtDate(currentLicense.expiration_date)}</span>
                    : <span style={{ color: 'var(--green)' }}>{currentLicense.days_remaining} jours restants</span>
                  }
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {currentLicense.has_license && currentLicense.all_modules?.map(mod => (
                <span 
                  key={mod} 
                  className={`badge ${currentLicense.is_expired ? 'badge-gray' : 'badge-green'}`}
                >
                  {modules[mod]?.name || mod}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Available Modules */}
      <div className="mb-6">
        <h3 className="text-sm font-bold uppercase text-[var(--text3)] mb-3">Modules disponibles</h3>
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(modules).map(([key, mod]) => (
            <div key={key} className="card">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                  <Package size={16} style={{ color: 'var(--accent)' }} />
                  <span className="font-bold">{mod.name}</span>
                </div>
                <p className="text-xs text-[var(--text3)] mb-3">{mod.description}</p>
                <div className="flex flex-wrap gap-1">
                  {mod.features?.map(f => (
                    <span key={f} className="text-xs px-2 py-0.5 rounded bg-[var(--bg3)]">{f}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Licenses Table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Toutes les licences</span>
          <span className="badge badge-gray">{licenses.length}</span>
        </div>
        <div className="card-body p-0">
          {licenses.length === 0 ? (
            <div className="text-center py-8 text-[var(--text3)]">
              Aucune licence créée
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Clé</th>
                  <th>Modules</th>
                  <th>Expiration</th>
                  <th>Utilisateurs</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map(lic => {
                  const expStatus = getExpirationStatus(lic.expiration_date);
                  return (
                    <tr key={lic.id}>
                      <td>
                        <div className="font-medium">{lic.client_name}</div>
                        <div className="text-xs text-[var(--text3)]">{lic.client_email}</div>
                      </td>
                      <td>
                        <span className="font-mono text-xs bg-[var(--bg3)] px-2 py-1 rounded">
                          {lic.license_key}
                        </span>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {lic.modules.map(m => (
                            <span key={m} className="badge badge-blue text-xs">{modules[m]?.name || m}</span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} style={{ color: expStatus.color }} />
                          <span>{fmtDate(lic.expiration_date)}</span>
                        </div>
                        <div className="text-xs" style={{ color: expStatus.color }}>{expStatus.label}</div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Users size={14} />
                          <span>{lic.max_users}</span>
                        </div>
                      </td>
                      <td>
                        {lic.is_active ? (
                          <span className="badge badge-green">Active</span>
                        ) : (
                          <span className="badge badge-gray">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button 
                            className="btn btn-ghost btn-sm text-red-500"
                            onClick={() => handleDelete(lic.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create License Modal */}
      {showCreateModal && (
        <div className="panel-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '550px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Nouvelle Licence</div>
              <button className="panel-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Nom client *</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={newLicense.client_name}
                    onChange={e => setNewLicense(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Garage Dupont SA"
                    data-testid="license-client-name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Email client *</label>
                  <input
                    type="email"
                    className="input w-full"
                    value={newLicense.client_email}
                    onChange={e => setNewLicense(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="contact@garage.ch"
                    data-testid="license-client-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Modules inclus *</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(modules).map(([key, mod]) => (
                    <button
                      key={key}
                      className={`p-3 rounded-lg text-left transition-all ${
                        newLicense.modules.includes(key)
                          ? 'bg-[var(--accent)]/20 border-2 border-[var(--accent)]'
                          : 'bg-[var(--bg3)] border-2 border-transparent hover:border-[var(--border)]'
                      }`}
                      onClick={() => toggleModule(key)}
                      data-testid={`module-toggle-${key}`}
                    >
                      <div className="font-medium text-sm">{mod.name}</div>
                      <div className="text-xs text-[var(--text3)]">{mod.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Date d'expiration *</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={newLicense.expiration_date}
                    onChange={e => setNewLicense(prev => ({ ...prev, expiration_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="license-expiration"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Max utilisateurs</label>
                  <input
                    type="number"
                    className="input w-full"
                    value={newLicense.max_users}
                    onChange={e => setNewLicense(prev => ({ ...prev, max_users: parseInt(e.target.value) || 5 }))}
                    min="1"
                    data-testid="license-max-users"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Notes</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  value={newLicense.notes}
                  onChange={e => setNewLicense(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes internes..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleCreate}
                  disabled={!newLicense.client_name || !newLicense.client_email || !newLicense.expiration_date || newLicense.modules.length === 0}
                  data-testid="submit-license-btn"
                >
                  <Check size={14} /> Créer la licence
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activate License Modal */}
      {showActivateModal && (
        <div className="panel-overlay" onClick={() => setShowActivateModal(false)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '400px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Activer une licence</div>
              <button className="panel-close" onClick={() => { setShowActivateModal(false); setActivateKey(''); setActivateError(''); }}>
                <X size={20} />
              </button>
            </div>

            <div className="text-center mb-6">
              <Key size={48} className="mx-auto mb-4" style={{ color: 'var(--accent)' }} />
              <p className="text-sm text-[var(--text3)]">
                Entrez la clé de licence fournie par No Name Motorcycles
              </p>
            </div>

            <input
              type="text"
              className="input w-full text-center font-mono text-lg tracking-widest uppercase"
              value={activateKey}
              onChange={e => { setActivateKey(e.target.value.toUpperCase()); setActivateError(''); }}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              maxLength={20}
              data-testid="activate-license-key"
            />

            {activateError && (
              <p className="text-center text-sm mt-2" style={{ color: 'var(--red)' }}>{activateError}</p>
            )}

            <button 
              className="btn btn-primary w-full mt-6"
              onClick={handleActivate}
              disabled={activateKey.length < 8}
              data-testid="submit-activate-btn"
            >
              <Key size={14} /> Activer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseAdmin;
